/**
 * 앵커된 액션 메뉴 — ARIA `menu`/`menuitem` 패턴의 compound primitive.
 *
 * - `Menu`: 컨텍스트 + open 상태(controlled/uncontrolled) 소유. popup은 c6 `AnchoredPopup`
 *   (`role="menu"`)로 띄운다 — 위치 보정·portal·outside-click/Escape dismiss·열림 시 focus 진입·
 *   닫힘 시 focus 복귀는 AnchoredPopup이 소유.
 * - `Menu.Trigger`: 자식이 없으면 기본 ⋯(kebab) IconButton, 자식이 있으면 그 단일 요소를 앵커로
 *   사용(asChild — onClick/ref/aria-* 주입).
 * - `Menu.Item` / `Menu.Separator` / `Menu.Section`: 메뉴 내용. 항목 활성화는 `onSelect`이며,
 *   기본은 선택 시 메뉴를 닫고 `event.preventDefault()`로 열린 상태 유지(토글·연속 선택).
 *
 * 키보드(APG menu): ↑/↓ roving(wrap), Home/End, 타입어헤드, Enter/Space 활성화(native button),
 * Tab은 메뉴를 닫고 자연 이동, Escape dismiss(AnchoredPopup). disabled 항목은 `aria-disabled`로
 * 두어 roving에는 포함(사유 caption을 키보드로 읽게)하되 활성화는 막는다.
 * 포인터: 항목에 hover하면 roving focus가 그 항목으로 이동(`focusOnHover`) — 키보드든 마우스든
 * 강조되는 "활성 항목"은 항상 하나로 유지된다.
 *
 * presentational 레이어는 `menuStyles.ts`, 순수 키보드 헬퍼는 `menuKeyboard.ts`로 분리.
 */
import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

import { AnchoredPopup, type AnchoredPopupAnchor } from "../AnchoredPopup";
import type { FloatingPopoverPlacement } from "../foundations/popover";
import {
  IconButton,
  type IconButtonSize,
  type IconButtonTone,
  type IconButtonVariant,
} from "../IconButton";
import { Text } from "../Text";
import { Tooltip, useTooltipController } from "../Tooltip";
import { MoreHorizontalIcon } from "../../icons/glyphs";
import {
  focusByTypeahead,
  focusRelative,
  isPrintableKey,
  type MenuTypeaheadState,
} from "./menuKeyboard";
import {
  ItemButton,
  ItemIcon,
  ItemTextColumn,
  MenuList,
  SectionGroup,
  SectionHeading,
  SeparatorLine,
  itemCaptionVariant,
  itemLabelVariant,
  sectionHeadingVariant,
} from "./menuStyles";

export type MenuItemTone = "default" | "warning" | "danger";

export interface MenuProps {
  /** `Menu.Trigger` 1개 + `Menu.Item`/`Menu.Separator`/`Menu.Section`. */
  children: ReactNode;
  /** 메뉴(popup)의 접근성 이름. Default `"Menu"`. */
  label?: string;
  /** controlled open 상태. 생략하면 uncontrolled. */
  open?: boolean;
  /** uncontrolled 초기 open. Default `false`. */
  defaultOpen?: boolean;
  /** open 변경 콜백(controlled/uncontrolled 공통). */
  onOpenChange?: (open: boolean) => void;
  /** 트리거 기준 배치. Default `"bottom-end"`. */
  placement?: FloatingPopoverPlacement;
  /** 메뉴 surface(Panel)에 적용. */
  className?: string;
  style?: CSSProperties;
}

interface MenuContextValue {
  open: boolean;
  triggerRef: MutableRefObject<HTMLElement | null>;
  toggle: (event: ReactMouseEvent) => void;
  close: () => void;
  onItemKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
}

const MenuContext = createContext<MenuContextValue | null>(null);

function useMenuContext(component: string): MenuContextValue {
  const context = useContext(MenuContext);
  if (context === null) {
    throw new Error(`[c6] <${component}> must be used within <Menu>.`);
  }
  return context;
}

function MenuRoot({
  children,
  label = "Menu",
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  placement = "bottom-end",
  className,
  style,
}: MenuProps) {
  const isControlled = openProp !== undefined;
  const [openState, setOpenState] = useState(defaultOpen);
  const open = isControlled ? openProp : openState;

  const triggerRef = useRef<HTMLElement | null>(null);
  const [anchor, setAnchor] = useState<AnchoredPopupAnchor | null>(null);
  const typeaheadRef = useRef<MenuTypeaheadState>({ buffer: "", timer: 0 });

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setOpenState(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  // 열려 있는 동안 트리거 위치를 anchor로 잡는다(외부 controlled open도 포함). 닫히면 해제.
  useLayoutEffect(() => {
    if (open && triggerRef.current) {
      const element = triggerRef.current;
      setAnchor({ type: "rect", rect: element.getBoundingClientRect(), el: element });
    } else if (!open) {
      setAnchor(null);
    }
  }, [open]);

  const close = useCallback(() => setOpen(false), [setOpen]);

  const toggle = useCallback(
    (event: ReactMouseEvent) => {
      // 테이블 행 액션 등 상위 클릭 핸들러로 전파 방지.
      event.stopPropagation();
      // 토글 직전 최신 위치를 잡아 첫 프레임부터 올바른 곳에 띄운다.
      const element = triggerRef.current;
      if (element) {
        setAnchor({ type: "rect", rect: element.getBoundingClientRect(), el: element });
      }
      setOpen(!open);
    },
    [open, setOpen],
  );

  const onItemKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      const item = event.currentTarget;
      const menu = item.closest<HTMLElement>('[role="menu"]');
      if (menu === null) {
        return;
      }
      const items = Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]'));
      if (items.length === 0) {
        return;
      }
      const index = items.indexOf(item);

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          focusRelative(items, index, 1);
          break;
        case "ArrowUp":
          event.preventDefault();
          focusRelative(items, index, -1);
          break;
        case "Home":
          event.preventDefault();
          items[0]?.focus();
          break;
        case "End":
          event.preventDefault();
          items[items.length - 1]?.focus();
          break;
        case "Tab":
          // APG: Tab은 메뉴를 닫고 포커스를 자연 이동(preventDefault 안 함).
          close();
          break;
        default: {
          const typeaheadState = typeaheadRef.current;
          if (isPrintableKey(event)) {
            if (event.key === " " && typeaheadState.buffer === "") {
              break;
            }
            event.preventDefault();
            focusByTypeahead(typeaheadState, items, index, event.key);
          }
        }
      }
    },
    [close],
  );

  const contextValue = useMemo<MenuContextValue>(
    () => ({ open, triggerRef, toggle, close, onItemKeyDown }),
    [open, toggle, close, onItemKeyDown],
  );

  const { trigger, items } = splitTriggerAndItems(children);

  return (
    <MenuContext.Provider value={contextValue}>
      {trigger}
      <AnchoredPopup
        open={open}
        anchor={anchor}
        onClose={close}
        role="menu"
        aria-label={label}
        placement={placement}
        // autoFocus는 AnchoredPopup의 focus 진입 정책(첫 focusable=첫 menuitem)이며 native autofocus가 아니다.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus="firstFocusable"
        returnFocus
        width="max-content"
        maxWidth="min(20rem, calc(100vw - 1rem))"
        className={className}
        style={style}
      >
        <MenuList role="presentation">{items}</MenuList>
      </AnchoredPopup>
    </MenuContext.Provider>
  );
}

MenuRoot.displayName = "Menu";

export interface MenuTriggerProps {
  /** asChild override — 단일 자식 요소를 트리거 앵커로 사용. 생략하면 기본 ⋯ IconButton. */
  children?: ReactElement;
  /** 기본 kebab 트리거의 접근성 이름. Default `"More actions"`. */
  label?: string;
  size?: IconButtonSize;
  tone?: IconButtonTone;
  variant?: IconButtonVariant;
  disabled?: boolean;
}

function MenuTrigger({
  children,
  label = "More actions",
  size = "sm",
  tone = "primary",
  variant = "surface",
  disabled = false,
}: MenuTriggerProps) {
  const context = useMenuContext("Menu.Trigger");
  // 메뉴 버튼은 aria-expanded로 상태를 알린다(aria-pressed는 토글 버튼 의미라 미사용).
  const ariaProps = { "aria-haspopup": "menu", "aria-expanded": context.open } as const;

  if (children != null) {
    const child = Children.only(children) as ReactElement<Record<string, unknown>>;
    const childOnClick = child.props.onClick as ((event: ReactMouseEvent) => void) | undefined;
    return cloneElement(child, {
      ref: mergeRefs(getElementRef(child), context.triggerRef),
      onClick: composeEventHandlers(childOnClick, context.toggle),
      ...ariaProps,
    });
  }

  return (
    <MenuKebabTrigger label={label} size={size} tone={tone} variant={variant} disabled={disabled} />
  );
}

MenuTrigger.displayName = "Menu.Trigger";

interface MenuKebabTriggerProps {
  label: string;
  size: IconButtonSize;
  tone: IconButtonTone;
  variant: IconButtonVariant;
  disabled: boolean;
}

/**
 * 기본 ⋯(kebab) 트리거. 라벨은 c6 `Tooltip`을 manual mode로 띄우되, 메뉴가 열려 있는 동안은 controller의
 * `suppressed`로 억제한다 — tooltip(z-index상 메뉴 위)이 열린 메뉴를 가리지 않게. 닫혀 있을 땐 hover/키보드
 * focus로 표시(`controller.triggerProps`).
 */
function MenuKebabTrigger({ label, size, tone, variant, disabled }: MenuKebabTriggerProps) {
  const context = useMenuContext("Menu.Trigger");
  const tooltip = useTooltipController({ suppressed: context.open });
  // 메뉴 버튼은 aria-expanded로 상태를 알린다(aria-pressed는 토글 버튼 의미라 미사용).
  const ariaProps = { "aria-haspopup": "menu", "aria-expanded": context.open } as const;

  return (
    <Tooltip content={label} controller={tooltip} describeTrigger={false}>
      <IconButton
        ref={(node) => {
          context.triggerRef.current = node;
        }}
        {...tooltip.triggerProps}
        aria-label={label}
        size={size}
        tone={tone}
        variant={variant}
        disabled={disabled}
        // 재클릭 토글은 click 하나로 충분하다 — 트리거는 anchor el이라 AnchoredPopup outside-dismiss 밖이 아니다.
        onClick={context.toggle}
        {...ariaProps}
      >
        <MoreHorizontalIcon />
      </IconButton>
    </Tooltip>
  );
}

MenuKebabTrigger.displayName = "MenuKebabTrigger";

export interface MenuItemProps {
  /** 항목 라벨. */
  children: ReactNode;
  /**
   * 활성화 핸들러. 기본은 선택 시 메뉴를 닫고 실행한다. `event.preventDefault()`를 호출하면
   * 메뉴를 닫지 않는다(토글·연속 선택). 반환값은 await하지 않는다.
   */
  onSelect?: (event: ReactMouseEvent<HTMLButtonElement>) => void | Promise<void>;
  /** 라벨 앞 아이콘(`ReactNode`). */
  icon?: ReactNode;
  /** semantic 톤. Default `"default"`. */
  tone?: MenuItemTone;
  /** 비활성 — 포커스는 되지만 활성화되지 않는다(`aria-disabled`). */
  disabled?: boolean;
  /** 라벨 아래 caption(비활성 사유 등). 항상 표시. */
  caption?: ReactNode;
  /** 렌더하지 않음(조건부 항목을 간결하게). */
  hidden?: boolean;
  className?: string;
}

function MenuItem({
  children,
  onSelect,
  icon,
  tone = "default",
  disabled = false,
  caption,
  hidden = false,
  className,
}: MenuItemProps) {
  const context = useMenuContext("Menu.Item");

  const handleClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (disabled) {
        return;
      }
      onSelect?.(event);
      // onSelect가 기본 동작을 막지 않았으면 닫는다.
      if (!event.defaultPrevented) {
        context.close();
      }
    },
    [disabled, onSelect, context],
  );

  if (hidden) {
    return null;
  }

  return (
    <ItemButton
      type="button"
      role="menuitem"
      tabIndex={-1}
      aria-disabled={disabled || undefined}
      data-tone={tone}
      $tone={tone}
      $disabled={disabled}
      className={className}
      title={resolveItemTitle(children, caption)}
      onClick={handleClick}
      onMouseEnter={focusOnHover}
      onKeyDown={context.onItemKeyDown}
    >
      {icon != null ? <ItemIcon aria-hidden="true">{icon}</ItemIcon> : null}
      <ItemTextColumn>
        <Text variant={itemLabelVariant}>{children}</Text>
        {caption != null ? <Text variant={itemCaptionVariant}>{caption}</Text> : null}
      </ItemTextColumn>
    </ItemButton>
  );
}

MenuItem.displayName = "Menu.Item";

function MenuSeparator() {
  return <SeparatorLine role="separator" />;
}

MenuSeparator.displayName = "Menu.Separator";

export interface MenuSectionProps {
  /** 그룹 접근성 이름 + 시각 헤딩. */
  label: string;
  children: ReactNode;
}

function MenuSection({ label, children }: MenuSectionProps) {
  return (
    <SectionGroup role="group" aria-label={label}>
      {/* 헤딩은 group의 aria-label로 이미 노출되므로 중복 announce 회피. */}
      <SectionHeading aria-hidden="true">
        <Text variant={sectionHeadingVariant}>{label}</Text>
      </SectionHeading>
      {children}
    </SectionGroup>
  );
}

MenuSection.displayName = "Menu.Section";

export const Menu = Object.assign(MenuRoot, {
  Trigger: MenuTrigger,
  Item: MenuItem,
  Separator: MenuSeparator,
  Section: MenuSection,
});

// --- 내부 헬퍼 ---------------------------------------------------------------

// hover한 항목으로 roving focus를 옮긴다. disabled(aria-disabled)도 native button이라 focus는
// 되며(활성화만 막힘), 키보드 roving이 disabled를 강조하는 것과 시각이 일치한다.
// 트레이드오프: 키보드로 강조를 다른 항목에 옮긴 뒤엔, 마우스가 머물던 항목 안에서 다시 움직여도
// 강조가 안 돌아온다(빠졌다 들어오거나 다른 항목으로 가야 회수). onMouseMove로 바꾸면 매 이동마다 즉시.
function focusOnHover(event: ReactMouseEvent<HTMLButtonElement>): void {
  event.currentTarget.focus();
}

function splitTriggerAndItems(children: ReactNode): { trigger: ReactNode; items: ReactNode[] } {
  let trigger: ReactNode = null;
  const items: ReactNode[] = [];
  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === MenuTrigger) {
      trigger = child;
    } else {
      items.push(child);
    }
  });
  return { trigger, items };
}

function resolveItemTitle(label: ReactNode, caption: ReactNode): string | undefined {
  if (typeof caption === "string") {
    return caption;
  }
  return typeof label === "string" ? label : undefined;
}

type ElementRef = Ref<HTMLElement> | undefined;

function mergeRefs(...refs: ElementRef[]): (node: HTMLElement | null) => void {
  return (node) => {
    for (const ref of refs) {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref != null) {
        (ref as MutableRefObject<HTMLElement | null>).current = node;
      }
    }
  };
}

function getElementRef(element: ReactElement<Record<string, unknown>>): ElementRef {
  // React 19에선 ref가 일반 prop. 구버전 호환 위해 props.ref와 element.ref 둘 다 본다.
  const fromProps = element.props.ref as ElementRef;
  const fromElement = (element as unknown as { ref?: ElementRef }).ref;
  return fromProps ?? fromElement;
}

function composeEventHandlers<E extends { defaultPrevented: boolean }>(
  theirHandler: ((event: E) => void) | undefined,
  ourHandler: (event: E) => void,
): (event: E) => void {
  return (event) => {
    theirHandler?.(event);
    if (!event.defaultPrevented) {
      ourHandler(event);
    }
  };
}
